import * as THREE from 'three';

export function init(scene, camera, renderer, controls, gui, config) {
    const cleanups = [];
    const updatables = [];

    // 1. Procedural Terrain Generation
    const size = 15000; // Extremely massive landscape
    const segments = 200; // Resolution
    const geometry = new THREE.PlaneGeometry(size, size, segments, segments);

    // Generate hills
    const positionAttribute = geometry.attributes.position;

    // We want a flat zone in the center for the 3D model
    const flatRadius = 500;
    const blendRadius = 800; // How far it takes to blend into the hills

    for (let i = 0; i < positionAttribute.count; i++) {
        const x = positionAttribute.getX(i);
        const y = positionAttribute.getY(i);

        // Distance from center
        const dist = Math.sqrt(x * x + y * y);

        if (dist > flatRadius) {
            // Complex sine wave noise for rolling hills
            let height =
                Math.sin(x / 400) * 150 +
                Math.cos(y / 600) * 250 +
                Math.sin((x + y) / 300) * 100 +
                Math.cos((x - y) / 200) * 50;

            // Avoid extreme negative valleys right at the base
            height = Math.max(-50, height + 100);

            // Blend scalar (0 at flatRadius, 1 at flatRadius + blendRadius)
            let blend = (dist - flatRadius) / blendRadius;
            blend = Math.min(1, Math.max(0, blend));

            // Apply smoothing curve (ease in)
            blend = blend * blend * (3 - 2 * blend);

            // Z in PlaneGeometry mapping becomes Y (height) when rotated to lay flat
            positionAttribute.setZ(i, (height * blend) - 5);
        } else {
            // Flat area is slightly buried to prevent Z-fighting with the site floor
            positionAttribute.setZ(i, -5);
        }
    }

    geometry.computeVertexNormals();

    const material = new THREE.MeshStandardMaterial({
        color: 0x333333, // Dark Grey
        roughness: 0.9,
        metalness: 0.05,
        flatShading: true // Gives a low-poly stylized look matching the site
    });

    const terrain = new THREE.Mesh(geometry, material);
    terrain.rotation.x = -Math.PI / 2; // Rotate flat onto the X/Z world plane
    terrain.position.y = -1; // Fine-tune base height so the site foundation rests on top
    terrain.receiveShadow = true;

    scene.add(terrain);

    // Add some background fog to blend the massive hills into the skybox
    scene.fog = new THREE.FogExp2(0xd6e5f8, 0.00015);

    cleanups.push(() => {
        geometry.dispose();
        material.dispose();
        scene.remove(terrain);
        scene.fog = null;
    });

    // 2. Smoke / Steam Plumes for Cooling Towers
    const smokeTextureCanvas = document.createElement('canvas');
    smokeTextureCanvas.width = 64;
    smokeTextureCanvas.height = 64;
    const ctx = smokeTextureCanvas.getContext('2d');
    const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    gradient.addColorStop(0, 'rgba(255,255,255,0.8)');
    gradient.addColorStop(0.5, 'rgba(255,255,255,0.4)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 64, 64);

    const smokeTexture = new THREE.CanvasTexture(smokeTextureCanvas);
    const smokeMaterial = new THREE.PointsMaterial({
        size: 25,
        map: smokeTexture,
        transparent: true,
        opacity: 0.3,
        depthWrite: false,
        blending: THREE.NormalBlending
    });

    // We'll create a few plume centers based on the cooling array locations
    const plumeCenters = [
        { x: 150, y: 70, z: 230 },   // NE Cooling Array
        { x: -150, y: 70, z: -150 }, // West Cooling Array
        //{ x: 100, y: 70, z: -50 }    // South Cooling Array
    ];

    const particleCountPerPlume = 150;
    const totalParticles = plumeCenters.length * particleCountPerPlume;

    const smokeGeo = new THREE.BufferGeometry();
    const smokePositions = new Float32Array(totalParticles * 3);
    const smokeLifetimes = new Float32Array(totalParticles); // 0 to 1
    const smokeVelocities = [];

    let pIdx = 0;
    for (let i = 0; i < plumeCenters.length; i++) {
        const center = plumeCenters[i];
        for (let j = 0; j < particleCountPerPlume; j++) {
            smokePositions[pIdx * 3] = center.x + (Math.random() - 0.5) * 40;
            smokePositions[pIdx * 3 + 1] = center.y + Math.random() * 100;
            smokePositions[pIdx * 3 + 2] = center.z + (Math.random() - 0.5) * 40;

            smokeLifetimes[pIdx] = Math.random();

            smokeVelocities.push({
                vx: (Math.random() - 0.5) * 5, // Wind drift X
                vy: 10 + Math.random() * 15,   // Upward draft rate
                vz: (Math.random() - 0.5) * 5, // Wind drift Z
                originX: center.x,
                originY: center.y,
                originZ: center.z
            });
            pIdx++;
        }
    }

    smokeGeo.setAttribute('position', new THREE.BufferAttribute(smokePositions, 3));
    const smokePoints = new THREE.Points(smokeGeo, smokeMaterial);
    scene.add(smokePoints);

    updatables.push((delta) => {
        const posAttr = smokeGeo.attributes.position;
        for (let i = 0; i < totalParticles; i++) {
            let lx = posAttr.getX(i);
            let ly = posAttr.getY(i);
            let lz = posAttr.getZ(i);

            const v = smokeVelocities[i];

            lx += v.vx * delta;
            ly += v.vy * delta;
            lz += v.vz * delta;

            smokeLifetimes[i] += delta * 0.15; // Approx 6.6 seconds lifespan

            if (smokeLifetimes[i] >= 1.0) {
                // Reset to base of the plume
                smokeLifetimes[i] = 0;
                lx = v.originX + (Math.random() - 0.5) * 40;
                ly = v.originY + (Math.random() - 0.5) * 10;
                lz = v.originZ + (Math.random() - 0.5) * 40;
            }

            posAttr.setXYZ(i, lx, ly, lz);
        }
        posAttr.needsUpdate = true;
    });

    cleanups.push(() => {
        smokeGeo.dispose();
        smokeMaterial.dispose();
        smokeTexture.dispose();
        scene.remove(smokePoints);
    });

    // 2.5. Double-Click Position Debugger
    const dblClickDebugEl = document.createElement('div');
    dblClickDebugEl.id = 'dblclick-debug';
    dblClickDebugEl.style.cssText = `
        position: fixed;
        bottom: 60px;
        left: 16px;
        background: rgba(0,0,0,0.75);
        color: #00ffcc;
        font-family: monospace;
        font-size: 13px;
        padding: 8px 12px;
        border-radius: 6px;
        pointer-events: none;
        z-index: 9999;
        display: none;
        line-height: 1.5;
    `;
    dblClickDebugEl.textContent = 'Double-click to get position';
    document.body.appendChild(dblClickDebugEl);

    const dblClickRaycaster = new THREE.Raycaster();
    const dblClickMouse = new THREE.Vector2();

    const onDblClick = (event) => {
        dblClickMouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        dblClickMouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
        dblClickRaycaster.setFromCamera(dblClickMouse, camera);

        // Test against everything in the scene
        const hits = dblClickRaycaster.intersectObjects(scene.children, true);
        if (hits.length > 0) {
            const p = hits[0].point;
            const r = (v) => Math.round(v * 10) / 10;
            dblClickDebugEl.textContent = `[DBL-CLICK] x:${r(p.x)}, y:${r(p.y)}, z:${r(p.z)}`;
            dblClickDebugEl.style.display = 'block';
            console.log(`[Scene4 DBL-CLICK] { x: ${r(p.x)}, y: ${r(p.y)}, z: ${r(p.z)} }`);
        }
    };

    window.addEventListener('dblclick', onDblClick);
    cleanups.push(() => {
        window.removeEventListener('dblclick', onDblClick);
        if (dblClickDebugEl.parentNode) dblClickDebugEl.parentNode.removeChild(dblClickDebugEl);
    });

    // 3. UI Customization for Scene 4
    const exploreInstr = document.getElementById('explore-instruction');
    const originalInstr = exploreInstr ? exploreInstr.innerHTML : '';

    if (exploreInstr) {
        exploreInstr.innerHTML = '• <strong>Explore:</strong> Click the 4 buttons';
    }


    const navContainer = document.getElementById('nav-zones-container');
    const zoneButtons = {
        'primary-power': document.getElementById('btn-primary-power'),
        'secondary-power': document.getElementById('btn-secondary-power'),
        'admin-logistics': document.getElementById('btn-admin-logistics'),
        'processing-area': document.getElementById('btn-processing-area')
    };

    const zoneViews = {
        'primary-power': {
            camera: { x: -20, y: 51, z: 303 },
            target: { x: 199, y: -26, z: 120 }
        },
        'secondary-power': {
            camera: { x: 36, y: 29, z: 319 },
            target: { x: -61, y: 10, z: 260 }
        },
        'admin-logistics': {
            camera: { x: 68, y: 89, z: 94 },
            target: { x: -10, y: 42, z: 29 }
        },
        'processing-area': {
            camera: { x: -31, y: 62, z: 12 },
            target: { x: -27, y: 60, z: 6 }
        }
    };

    // Show nav buttons immediately
    if (navContainer) {
        navContainer.style.display = 'flex';
        navContainer.style.opacity = '1';
    }

    const goToZone = (zoneId) => {
        console.log(`[Scene4] goToZone triggered for: ${zoneId}`);
        const view = zoneViews[zoneId];
        if (!view) { console.warn(`[Scene4] No view found for zone: ${zoneId}`); return; }

        if (window.SceneManager && window.SceneManager.zoomToView) {
            window.SceneManager.zoomToView(view.camera, view.target, 2000);
        } else {
            console.error("[Scene4] SceneManager.zoomToView is NOT available!");
        }
    };

    // Add click listeners
    Object.keys(zoneButtons).forEach(id => {
        const btn = zoneButtons[id];
        if (!btn) return;

        const handler = () => goToZone(id);
        btn.addEventListener('click', handler);
        cleanups.push(() => btn.removeEventListener('click', handler));
    });

    // Cleanup UI
    cleanups.push(() => {
        if (navContainer) navContainer.style.display = 'none';
        if (exploreInstr) exploreInstr.innerHTML = originalInstr;
    });
    return {
        update: (delta, elapsed) => {
            updatables.forEach(fn => fn(delta));
        },
        dispose: () => {
            cleanups.forEach(fn => fn());
        }
    };
}
